import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { z } from "zod";
import { createHash } from "crypto";

const schema = z.object({
  email: z.string().trim().toLowerCase().email().max(255),
  frequency: z.enum(["daily", "weekly", "monthly"]).default("daily"),
});

const RATE_LIMIT_MAX = 5;
const RATE_WINDOW_MS = 60 * 60 * 1000;
const SITE_URL = "https://flaneriet.online";

export const subscribe = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => schema.parse(data))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { enqueueTemplateEmail } = await import("@/lib/email/send-internal.server");

    const req = getRequest();
    const ipRaw =
      req?.headers.get("cf-connecting-ip") ||
      req?.headers.get("x-forwarded-for")?.split(",")[0]?.trim() ||
      req?.headers.get("x-real-ip") ||
      "unknown";
    const ipHash = createHash("sha256").update(ipRaw).digest("hex");

    const sinceIso = new Date(Date.now() - RATE_WINDOW_MS).toISOString();
    const { count } = await supabaseAdmin
      .from("subscribe_attempts")
      .select("id", { count: "exact", head: true })
      .eq("ip_hash", ipHash)
      .gte("created_at", sinceIso);

    if ((count ?? 0) >= RATE_LIMIT_MAX) {
      throw new Error("For mange forsøg. Prøv igen senere.");
    }

    await supabaseAdmin.from("subscribe_attempts").insert({ ip_hash: ipHash });

    // Insert or fetch existing (unconfirmed) subscriber
    const { data: existing } = await supabaseAdmin
      .from("subscribers")
      .select("id, confirm_token, confirmed")
      .eq("email", data.email)
      .maybeSingle();

    let confirmToken: string | undefined;
    let already = false;

    if (existing) {
      // Update frequency preference even for existing rows
      await supabaseAdmin
        .from("subscribers")
        .update({ frequency: data.frequency })
        .eq("id", existing.id);
      if (existing.confirmed) {
        return { ok: true, already: true as const };
      }
      confirmToken = existing.confirm_token as string;
      already = true;
    } else {
      const { data: inserted, error } = await supabaseAdmin
        .from("subscribers")
        .insert({ email: data.email, confirmed: false, frequency: data.frequency })
        .select("confirm_token")
        .single();
      if (error) throw new Error(error.message);
      confirmToken = inserted?.confirm_token as string;
    }

    if (confirmToken) {
      const confirmUrl = `${SITE_URL}/tilmeld/bekraeft?token=${confirmToken}`;
      // Fire-and-forget – but await so errors surface for logging
      const res = await enqueueTemplateEmail({
        templateName: "confirm-subscription",
        recipientEmail: data.email,
        idempotencyKey: `confirm-${confirmToken}`,
        templateData: { confirmUrl, siteUrl: SITE_URL },
      });
      if (!res.ok && res.reason !== "suppressed") {
        console.error("Failed to enqueue confirmation email", res);
      }
    }

    return { ok: true, already };
  });
